import { Rect } from '../math/Rect';
import { Vector2 } from '../math/Vector2';
import { type Camera } from './Camera';
import { computeViewport, type Viewport } from './Viewport';

const DEFAULT_CSS_WIDTH = 1280;
const DEFAULT_CSS_HEIGHT = 720;

/**
 * A flat, non-perspective camera: the only kind a 2D game needs.
 *
 * It shows exactly the viewport's design height in world units vertically and lets the horizontal
 * extent follow the window, so content authored once fits every screen without a layout pass. There is
 * no zoom and no rotation yet; both are additions rather than changes when a game asks for them.
 */
export class OrthographicCamera implements Camera {
  /** Where the camera sits in world units; the world point drawn at the center of the viewport. */
  readonly position = Vector2.zero();

  #viewport: Viewport;

  constructor(viewport: Viewport = computeViewport(DEFAULT_CSS_WIDTH, DEFAULT_CSS_HEIGHT)) {
    this.#viewport = viewport;
  }

  /** The area of the canvas this camera draws into, and how much world that shows. */
  get viewport(): Viewport {
    return this.#viewport;
  }

  /** Adopts a new viewport, normally because the canvas was resized. */
  setViewport(viewport: Viewport): void {
    this.#viewport = viewport;
  }

  /** The world rectangle currently visible, centered on {@link OrthographicCamera.position}. */
  getVisibleBounds(out: Rect = new Rect()): Rect {
    const { worldWidth, worldHeight } = this.#viewport;
    return out.set(this.position.x - worldWidth / 2, this.position.y - worldHeight / 2, worldWidth, worldHeight);
  }

  /**
   * Converts a point in CSS pixels from the canvas top left into world units.
   *
   * Points inside the letterbox bars map to world positions outside the visible bounds, which is the
   * honest answer: they are outside the playfield.
   */
  screenToWorld(screenX: number, screenY: number, out: Vector2 = new Vector2()): Vector2 {
    const viewport = this.#viewport;
    const fractionX = (screenX - viewport.offsetX) / viewport.width;
    const fractionY = (screenY - viewport.offsetY) / viewport.height;

    return out.set(
      this.position.x + (fractionX - 0.5) * viewport.worldWidth,
      this.position.y + (0.5 - fractionY) * viewport.worldHeight,
    );
  }

  /** Converts a world point into CSS pixels from the canvas top left. */
  worldToScreen(worldX: number, worldY: number, out: Vector2 = new Vector2()): Vector2 {
    const viewport = this.#viewport;
    const fractionX = (worldX - this.position.x) / viewport.worldWidth + 0.5;
    const fractionY = 0.5 - (worldY - this.position.y) / viewport.worldHeight;

    return out.set(viewport.offsetX + fractionX * viewport.width, viewport.offsetY + fractionY * viewport.height);
  }
}
